import React, { useState, useEffect } from 'react';
import { ChatTheme } from '@/types/theme';
import { Message } from '@/types/chat';
import { sanitizeContent, processMessageContent } from '@/utils/aiUtils';
import { TCPAWarning } from '@/components/chat/TCPAWarning';

/**
 * Props for StreamingMessageBubble component
 * Following OfferChat.ai TypeScript best practices with explicit interfaces
 */
interface StreamingMessageBubbleProps {
  text: string;
  isStreaming: boolean;
  sender: 'user' | 'bot';
  theme?: ChatTheme;
  typingSpeed?: number;
  className?: string;
  timestamp?: Date;
  messageId?: string;
  metadata?: Record<string, any>;
}

/**
 * A message bubble component that supports real-time streaming of text
 * Follows OfferChat.ai UI component patterns
 */
export function StreamingMessageBubble({
  text,
  isStreaming,
  sender,
  theme,
  typingSpeed = 400,
  className = '',
  timestamp,
  messageId,
  metadata
}: StreamingMessageBubbleProps) {
  const [cursorVisible, setCursorVisible] = useState(true);

  // Animate cursor blinking
  useEffect(() => {
    if (!isStreaming) {
      setCursorVisible(false);
      return;
    }

    // Blink cursor when streaming
    const cursorInterval = setInterval(() => {
      setCursorVisible(prev => !prev);
    }, 500);

    return () => {
      clearInterval(cursorInterval);
    };
  }, [isStreaming]);

  // Determine bubble style based on sender and theme
  const getBubbleStyle = () => {
    const isUser = sender === 'user';

    return {
      backgroundColor: isUser ?
        theme?.userbubblecolor || '#497CFD' :
        theme?.botbubblecolor || '#F5F5F5',
      color: isUser ?
        theme?.usertextcolor || '#FFFFFF' :
        theme?.bottextcolor || '#000000',
      borderRadius: theme?.borderradius ? `${theme.borderradius}px` : '1rem',
      borderBottomRightRadius: isUser ? '0.25rem' : theme?.borderradius ? `${theme.borderradius}px` : '1rem',
      borderBottomLeftRadius: isUser ? theme?.borderradius ? `${theme.borderradius}px` : '1rem' : '0.25rem',
      padding: '0.75rem 1rem',
      maxWidth: '100%',
      minWidth: '60px',
      width: 'auto',
      wordBreak: sender === 'user' ? 'normal' : 'break-word' as const,
      whiteSpace: 'pre-wrap' as const,
      overflowWrap: sender === 'user' ? 'break-word' : 'anywhere' as const,
      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
      position: 'relative' as const,
      margin: '0.25rem 0',
      lineHeight: '1.5'
    };
  };

  // Extract question metadata if available
  const questionId = metadata?.questionId;
  const questionType = metadata?.questionType;
  const questionIndex = metadata?.questionIndex;
  const isTcpaWarning = metadata?.isTcpaWarning;

  // Handle TCPA warning messages specially
  if (isTcpaWarning) {
    // Extract the TCPA message content (remove the warning prefix)
    const tcpaContent = text.replace(/^⚠️ \*\*TCPA Notice\*\*\n\n/, '');
    
    return (
      <div
        className={`message-bubble tcpa-warning ${className}`}
        data-testid="tcpa-warning"
        data-message-id={messageId}
        style={{ margin: '0.25rem 0' }}
      >
        <TCPAWarning message={tcpaContent} />
      </div>
    );
  }

  return (
    <div
      className={`message-bubble ${sender}-bubble ${className} ${isStreaming ? 'streaming' : ''}`}
      style={getBubbleStyle()}
      data-testid={`${sender}-message`}
      data-message-id={messageId}
      data-question-id={questionId}
      data-question-type={questionType}
      data-question-index={questionIndex}
    >
      {/* Process and sanitize content for rich media support */}
      <div
        className={`message-content ${sender}-content`}
        style={{
          width: '100%',
          overflowWrap: sender === 'user' ? 'break-word' : 'anywhere',
          wordBreak: sender === 'user' ? 'break-word' : 'break-word',
          whiteSpace: 'pre-wrap',
          hyphens: 'none'
        }}
        dangerouslySetInnerHTML={{
          __html: sanitizeContent(processMessageContent(text, metadata?.rich_content))
        }}
      />

      {/* Typing cursor */}
      {isStreaming && cursorVisible && (
        <span
          className="typing-cursor"
          aria-hidden="true"
          style={{
            display: 'inline-block',
            width: '2px',
            height: '1em',
            backgroundColor: 'currentColor',
            marginLeft: '2px',
            verticalAlign: 'middle',
            animation: 'blink 1s step-end infinite'
          }}
        />
      )}
    </div>
  );
}
